import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/RichTextEditor";
import { RecipientPicker } from "./RecipientPicker";
import { EmailPreview } from "./EmailPreview";

function Owner({ d }) {
  if (!d.partner) {
    return (
      <span className="flex items-center gap-1.5 text-amber-700" data-testid="pw-owner-missing">
        <AlertTriangle className="h-3.5 w-3.5" /> No partner found for territory {d.territory || "(none)"} — type the recipients below.
      </span>
    );
  }
  return (
    <span data-testid="pw-owner">
      Owner: <b className="text-slate-700">{d.partner.name}</b>{d.partner.partner_type ? ` (${d.partner.partner_type})` : ""} · territory {d.territory}
      <span className="text-slate-400"> · matched by {d.partner.matched_by}</span>
    </span>
  );
}

/** Emergency Numbers → domain → Warn partner: review, edit and send the 911 warning e-mail. */
export function PartnerWarningDialog({ target, open, onOpenChange }) {
  const [d, setD] = useState(null);
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tab, setTab] = useState("preview");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const base = target ? `/e911/domains/${target.platform}/${encodeURIComponent(target.domain)}/partner-warning` : "";
  const reset = (data) => { setTo(data.recipients); setSubject(data.subject); setBody(data.body_html); };

  useEffect(() => {
    if (!open || !base) return;
    setD(null); setTab("preview"); setConfirm(false);
    api.get(base).then(({ data }) => { setD(data); reset(data); })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not build the warning"); onOpenChange(false); });
  }, [open, base]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/send`, { to: to.map((r) => r.email), subject, body_html: body });
      if (data.status === "paused") toast.warning("E-mail sending is paused, so the warning is held in Settings → Platform → Outbound email");
      else toast.success(`Warning sent to ${data.to.length} recipient${data.to.length === 1 ? "" : "s"} — ${data.cc.join(", ") || "support"} copied`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the warning");
      setConfirm(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="pw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><AlertTriangle className="h-5 w-5 text-rose-600" /> Warn the partner about {target?.domain}</DialogTitle>
          <DialogDescription asChild><div className="text-xs text-slate-500">{d ? <Owner d={d} /> : "Building the message from the last assessment…"}</div></DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-12 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-300" /></div> : (
          <div className="space-y-4">
            <section>
              <div className="mb-1.5 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <span>To</span>{d.recipient_scope && <span className="normal-case tracking-normal text-slate-400">pre-filled with the {d.recipient_scope}</span>}
              </div>
              <RecipientPicker value={to} onChange={setTo} options={d.partner_users} />
              <p className="mt-2 text-xs text-slate-500" data-testid="pw-cc">CC <b>{d.cc.join(", ")}</b> · replies go to <b>{d.reply_to}</b></p>
            </section>
            <section>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-400" htmlFor="pw-subject">Subject</label>
              <Input id="pw-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={250} data-testid="pw-subject" />
            </section>
            <Tabs value={tab} onValueChange={setTab}>
              <div className="flex items-center justify-between">
                <TabsList><TabsTrigger value="preview" data-testid="pw-tab-preview">Preview</TabsTrigger><TabsTrigger value="edit" data-testid="pw-tab-edit">Edit message</TabsTrigger></TabsList>
                <Button size="sm" variant="ghost" onClick={() => reset(d)} data-testid="pw-reset"><RotateCcw className="h-3.5 w-3.5" /> Reset to the generated text</Button>
              </div>
              <TabsContent value="preview"><EmailPreview base={base} subject={subject} body={body} /></TabsContent>
              <TabsContent value="edit">
                <div className="[&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5">
                  <RichTextEditor value={body} onChange={setBody} testid="pw-body-editor" />
                </div>
              </TabsContent>
            </Tabs>
            {!d.email_enabled && <p className="text-xs text-rose-600" data-testid="pw-email-off">E-mail is not configured on this server, so sending will fail.</p>}
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3" data-testid="pw-footer">
              {confirm ? (
                <>
                  <span className="mr-auto text-sm text-slate-700" data-testid="pw-confirm-text">Send this warning to <b>{to.length}</b> recipient{to.length === 1 ? "" : "s"} now?</span>
                  <Button variant="outline" onClick={() => setConfirm(false)} disabled={busy} data-testid="pw-confirm-back">Back</Button>
                  <Button onClick={send} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="pw-confirm-send">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Yes, send it</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pw-cancel">Cancel</Button>
                  <Button onClick={() => setConfirm(true)} disabled={!to.length || !subject.trim()} className="bg-rose-600 hover:bg-rose-700" data-testid="pw-send">
                    <Send className="h-4 w-4" /> Send warning</Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
